import React, { useState, useCallback } from 'react';
import { GenericStep } from '@/components/FullWidthTemplate/GenericStep';
import { IconStarsOutline, IconWordpress, TextField } from 'gatorcomponents';
import * as Styles from './WordPressInstall.styles';
import WordPressAiBr from '@/media/onboarding/wordpress/wordpress-ai-br.png';
import WordPressAiEs from '@/media/onboarding/wordpress/wordpress-ai-es.png';
import WordPressBr from '@/media/onboarding/wordpress/wordpress-br.png';
import WordPressEs from '@/media/onboarding/wordpress/wordpress-es.png';
import { isBRServer } from '@/utils/Validators/validation';
import { onboardingAnalytics } from '@/analytics/onboarding';
import { FieldValidators } from '@/utils/FieldValidators';

const WordPressInstall = ({
  buttonsProps,
  onSubmit,
  testId = 'wordpress-install-step',
  isWordPressAI,
  wordpressLoading,
  currentFlow,
  locales,
  FLOWS,
}) => {
  const [loginForm, setLoginForm] = useState({
    userName: {
      value: '',
      error: '',
    },
    password: {
      value: '',
      error: '',
    },
  });
  const [hasAllRequirements, setHasAllRequirements] = useState(false);

  const validatePassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W)[A-Za-z\d\W]{8,}$/;
    return regex.test(password);
  };

  const validateUserName = (userName) => {
    const minLengthValidation = userName.length >= 6;
    const specialCharValidation = FieldValidators.userWordpressHasNotSpecial(userName);

    return minLengthValidation && specialCharValidation;
  };

  const validateFormRules = (formData) => {
    const isValidUserName = validateUserName(formData.userName.value);
    const isValidPassword = validatePassword(formData.password.value);

    setHasAllRequirements(isValidUserName && isValidPassword);

    return {
      isValidUserName,
      isValidPassword,
    };
  };

  const getFieldErrors = (field, validations) => {
    if (field === 'userName' && !validations.isValidUserName) {
      return locales.userNameError;
    }
    if (field === 'password' && !validations.isValidPassword) {
      return locales.passwordError;
    }

    return '';
  };

  const handleChange = ({ field, value }) => {
    const newFormData = { ...loginForm };
    newFormData[field].value = value;

    const validations = validateFormRules(newFormData);
    const error = getFieldErrors(field, validations);

    setLoginForm({
      ...loginForm,
      [field]: {
        value,
        error,
      },
    });
  };

  const userInputAnalytic = () => {
    const analyticRules = {
      [FLOWS.WORDPRESS_IA]: onboardingAnalytics.wordpressIa.user,
      [FLOWS.WORDPRESS]: onboardingAnalytics.wordpressDefault.user,
    };

    const userOnBlur = analyticRules[currentFlow];

    const usernameIsValid = loginForm.userName.error === '';
    const usernameIsNotEmpty = loginForm.userName.value !== '';

    if (usernameIsValid && usernameIsNotEmpty) {
      userOnBlur && userOnBlur();
    }
  };

  const passwordInputAnalytic = () => {
    const analyticRules = {
      [FLOWS.WORDPRESS_IA]: onboardingAnalytics.wordpressIa.password,
      [FLOWS.WORDPRESS]: onboardingAnalytics.wordpressDefault.password,
    };

    const passwordOnBlur = analyticRules[currentFlow];

    const passwordIsValid = loginForm.password.error === '';
    const passwordIsNotEmpty = loginForm.password.value !== '';

    if (passwordIsValid && passwordIsNotEmpty) {
      passwordOnBlur && passwordOnBlur();
    }
  };

  const handleOnSubmit = useCallback(() => {
    const { userName, password } = loginForm;
    onSubmit && onSubmit({ admin: userName.value, password: password.value });
  }, [loginForm, onSubmit]);

  const stepProps = useCallback(() => {
    const WordPressAIImage = isBRServer ? WordPressAiBr : WordPressAiEs;
    const WordPressImage = isBRServer ? WordPressBr : WordPressEs;

    const customButtonProps = {
      ...buttonsProps,
      rightButton: {
        ...buttonsProps.rightButton,
        testId: 'continue-button',
        disabled: !hasAllRequirements || wordpressLoading,
        loading: wordpressLoading,
        onClick: handleOnSubmit,
      },
    };

    return {
      icon: isWordPressAI ? <IconStarsOutline color="primaryMedium" /> : <IconWordpress color="primaryMedium" size="lg" />,
      title: locales.title,
      testId,
      subtitle: isWordPressAI ? locales.subtitle : '',
      description: locales.description,
      imageSrc: isWordPressAI ? WordPressAIImage : WordPressImage,
      buttonsProps: customButtonProps,
    };
  }, [buttonsProps, hasAllRequirements, locales, isWordPressAI, wordpressLoading, handleOnSubmit, testId]);

  return (
    <GenericStep {...stepProps()}>
      <Styles.Login>
        <TextField
          type="text"
          label={locales.userLabel}
          status={loginForm.userName.error ? 'error' : 'normal'}
          helpText={loginForm.userName.error}
          onBlur={userInputAnalytic}
          onChange={e => handleChange({ field: 'userName', value: e.target.value })}
          value={loginForm.userName.value}
          testId="user-input"
        />
        <TextField
          type="password"
          label={locales.passwordLabel}
          status={loginForm.password.error ? 'error' : 'normal'}
          helpText={loginForm.password.error}
          onBlur={passwordInputAnalytic}
          onChange={e => handleChange({ field: 'password', value: e.target.value })}
          value={loginForm.password.value}
          testId="password-input"
        />
      </Styles.Login>
    </GenericStep>
  );
};

export default WordPressInstall;
